import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { astraAPI } from '../../../services/astraApi';
import { SkeletonList } from '../../../components/ui/Skeleton';

/**
 * Recent Trades - COMPACT VIEW (2025-12-30)
 *
 * Redesigned for the optimized dashboard layout.
 * Now displays as a full-width activity log with compact cards.
 */

const RecentTrades = ({ refreshTrigger = 0 }) => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [tradesData, setTradesData] = useState([]);
  const [loading, setLoading] = useState(true);
  // 🔧 2025-12-30 FIX: Prevent flickering on refresh - only show skeleton on INITIAL load
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRecentTrades();
  }, [refreshTrigger]);

  const fetchRecentTrades = async () => {
    try {
      // 🔧 FIX: Only show loading skeleton on initial load, not on refreshes
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      let recentTrades = [];

      // Use real trading orders API
      try {
        const ordersData = await astraAPI.getTradingOrders('all', 10, 1453);
        if (ordersData && ordersData.length > 0) {
          recentTrades = ordersData.slice(0, 6).map((order) => ({
            id: order.id || order.order_id,
            symbol: order.symbol,
            type: (order.side || '').toUpperCase(),
            quantity: order.quantity,
            price: order.price,
            value: order.value || (order.quantity * order.price),
            timestamp: new Date(order.timestamp || order.created_at),
            status: (order.status || 'PENDING').toUpperCase(),
            aiConfidence: order.confidence || 0.5
          }));
        } else {
          // Fallback to trade history if orders unavailable
          const tradeHistory = await astraAPI.getTradeHistory(1453, 6);
          if (tradeHistory && tradeHistory.items) {
            recentTrades = tradeHistory.items.map((trade) => ({
              id: trade.id,
              symbol: trade.symbol,
              type: (trade.side || trade.action || 'BUY').toUpperCase(),
              quantity: trade.quantity,
              price: trade.price || trade.entry_price,
              value: trade.amount || (trade.quantity * (trade.price || trade.entry_price)),
              timestamp: new Date(trade.timestamp || trade.entry_time || trade.created_at),
              status: (trade.status || 'COMPLETED').toUpperCase(),
              aiConfidence: trade.confidence || 0.5
            }));
          }
        }
      } catch (apiErr) {
        console.error('Failed to fetch trading data:', apiErr);
        setError('Unable to load recent trades from API');
      }

      setTradesData(recentTrades);
    } catch (err) {
      console.error('Failed to fetch recent trades:', err);
      setError('Failed to load recent trades');
      setTradesData([]);
    } finally {
      setLoading(false);
      // 🔧 FIX: Mark initial load complete - subsequent refreshes won't show skeleton
      setIsInitialLoad(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatTime = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString('en-IN');
  };

  const filteredTrades = tradesData.filter(trade => {
    if (selectedFilter === 'all') return true;
    if (selectedFilter === 'buy') return trade.type === 'BUY';
    if (selectedFilter === 'sell') return trade.type === 'SELL';
    return true;
  });

  // 🔧 2025-12-30 FIX: Only show skeleton on INITIAL load, not on refreshes
  if (loading && isInitialLoad) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-card border border-border rounded-lg p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Recent Trades</h2>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-warning rounded-full animate-live-pulse"></div>
            <span className="text-xs text-muted-foreground">Loading...</span>
          </div>
        </div>
        <SkeletonList items={4} />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-card border border-border rounded-lg p-6 glass-card-hover"
    >
      {/* Header with filter tabs */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <h2 className="text-lg font-semibold text-foreground">Recent Activity</h2>
          <div className="flex space-x-1 bg-muted rounded-lg p-0.5">
            {['all', 'buy', 'sell'].map((filter) => (
              <button
                key={filter}
                onClick={() => setSelectedFilter(filter)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  selectedFilter === filter
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {filter === 'all' ? 'All' : filter === 'buy' ? '▲ Buy' : '▼ Sell'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-success rounded-full animate-live-pulse"></div>
          <span className="text-xs text-muted-foreground">{filteredTrades.length} trades</span>
        </div>
      </div>

      {/* Compact horizontal trades list */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <AnimatePresence>
          {filteredTrades.map((trade, index) => (
            <motion.div
              key={trade.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2, delay: index * 0.03 }}
              className={`
                flex items-center justify-between p-3 rounded-lg border transition-all hover:shadow-sm
                ${trade.type === 'BUY'
                  ? 'border-success/30 bg-success/5'
                  : 'border-error/30 bg-error/5'
                }
              `}
            >
              {/* Left: Symbol + Type */}
              <div className="flex items-center space-x-3">
                <div className={`
                  w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs
                  ${trade.type === 'BUY'
                    ? 'bg-success/20 text-success'
                    : 'bg-error/20 text-error'
                  }
                `}>
                  {trade.type === 'BUY' ? '▲' : '▼'}
                </div>
                <div>
                  <div className="font-medium text-foreground text-sm">{trade.symbol}</div>
                  <div className="text-xs text-muted-foreground">
                    {trade.quantity} @ {trade.price > 0 ? `₹${trade.price.toLocaleString()}` : 'Market'}
                  </div>
                </div>
              </div>

              {/* Right: Value + Time */}
              <div className="text-right">
                <div className="font-semibold text-foreground text-sm">
                  {trade.value > 0 ? formatCurrency(trade.value) : '-'}
                </div>
                <div className="text-xs text-muted-foreground">{formatTime(trade.timestamp)}</div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {filteredTrades.length === 0 && (
          <div className="col-span-full text-center py-8 bg-muted/30 rounded-lg">
            <Icon name="Activity" size={24} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No recent trades</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default RecentTrades;